import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO Component for Flowingo AB
 * Handles meta tags, structured data (Schema.org), and Open Graph
 */

// Default SEO configuration
const defaultSEO = {
  siteName: 'Flowingo AB',
  siteUrl: 'https://flowingo.se',
  defaultTitle: 'Flowingo AB | Mätare, expansionssystem & tankar för VVS-proffs',
  defaultDescription: 'Flowingo AB är Sveriges ledande B2B-leverantör av VVS-produkter. Specialister på mätare, expansionssystem och tankar. Vi erbjuder även pumpar, ventiler, värmeväxlare och automatisering för installatörer och grossister.',
  defaultImage: 'https://flowingo.se/og-image.jpg',
  locale: 'sv_SE',
  type: 'website',
};

// Organization Schema (site-wide)
const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': 'https://flowingo.se/#organization',
  name: 'Flowingo AB',
  alternateName: 'Flowingo',
  url: 'https://flowingo.se',
  logo: {
    '@type': 'ImageObject',
    url: 'https://flowingo.se/logo.png',
    width: 180,
    height: 40,
  },
  description: 'Leverantör av VVS-produkter för proffsmarknaden sedan 2022. Specialister på mätare, expansionssystem och tankar. Komplett sortiment av pumpar, ventiler, värmeväxlare och automatiseringslösningar.',
  address: {
    '@type': 'PostalAddress',
    streetAddress: 'Solventilsgatan 17D',
    addressLocality: 'Göteborg',
    postalCode: '417 24',
    addressCountry: 'SE',
  },
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+46-31-27-32-32',
    contactType: 'customer service',
    availableLanguage: ['Swedish', 'English'],
  },
  sameAs: [
    'https://www.linkedin.com/company/flowingo',
  ],
  vatID: 'SE559403766401',
  taxID: '559403-7664',
  areaServed: {
    '@type': 'Country',
    name: 'Sweden',
  },
  knowsAbout: [
    // Primära produkter (högst prioritet)
    'Mätare för VVS',
    'Expansionssystem',
    'Tankar och tryckkärl',
    // Övriga produktkategorier
    'Återströmningsskydd',
    'Luft- och partikelavskiljare',
    'Värmeväxlare',
    'Ventiler',
    'Automatisering och manövrering',
    'Pumpar och cirkulationspumpar',
    // Allmänna termer
    'VVS-produkter',
    'B2B VVS-leverantör',
    'VVS-grossist Sverige',
  ],
};

// WebSite Schema with SearchAction
const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': 'https://flowingo.se/#website',
  url: 'https://flowingo.se',
  name: 'Flowingo AB',
  description: 'VVS-produkter och konsultuppdrag för proffsmarknaden',
  publisher: {
    '@id': 'https://flowingo.se/#organization',
  },
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: 'https://flowingo.se/produkter?search={search_term_string}',
    },
    'query-input': 'required name=search_term_string',
  },
  inLanguage: ['sv-SE', 'en'],
};

/**
 * Generate breadcrumb schema
 */
export const generateBreadcrumbSchema = (items) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url,
    })),
  };
};

const smartTruncate = (text, max = 200) => {
  const t = (text || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (t.length <= max) return t;
  const cut = t.slice(0, max);
  return cut.slice(0, cut.lastIndexOf(' ')) + '…';
};

/**
 * Generate product schema (B2B quote-based catalog — Offer without price)
 */
export const generateProductSchema = (product, language = 'sv', categoryName = '') => {
  const name = language === 'sv' ? product.name_sv : product.name_en;
  const description = language === 'sv' ? product.description_sv : product.description_en;
  const url = `https://flowingo.se/produkter/${product.slug || product.id}`;
  const firstVariant = product.variants?.[0] || {};
  const artKey = Object.keys(firstVariant).find((k) => k.trim().toLowerCase().includes('artikelnummer'));
  const articleNumber = artKey ? firstVariant[artKey] : null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    '@id': url,
    url: url,
    name: name,
    description: smartTruncate(description, 200),
    image: product.image_url || 'https://flowingo.se/product-placeholder.jpg',
    offers: {
      '@type': 'Offer',
      url: url,
      availability: 'https://schema.org/InStock',
      businessFunction: 'http://purl.org/goodrelations/v1#Sell',
      seller: { '@id': 'https://flowingo.se/#organization' },
      priceSpecification: {
        '@type': 'PriceSpecification',
        valueAddedTaxIncluded: false,
        description: language === 'sv' ? 'Pris på begäran / offertförfrågan krävs' : 'Price on request / quote required',
      },
    },
  };
  if (categoryName) schema.category = categoryName;
  if (articleNumber) {
    schema.sku = String(articleNumber);
    schema.mpn = String(articleNumber);
  }
  if (product.brand) {
    schema.brand = { '@type': 'Brand', name: product.brand };
    schema.manufacturer = { '@type': 'Organization', name: product.brand };
  }
  return schema;
};

/**
 * Generate job posting schema
 */
export const generateJobPostingSchema = (job, language = 'sv') => {
  const title = language === 'sv' ? job.title_sv : job.title_en;
  const description = language === 'sv' 
    ? (job.description_sv || job.summary_sv) 
    : (job.description_en || job.summary_en);
  
  return {
    '@context': 'https://schema.org',
    '@type': 'JobPosting',
    '@id': `https://flowingo.se/jobs/${job.id}`,
    title: title,
    description: description?.replace(/<[^>]*>/g, '').slice(0, 500),
    datePosted: job.created_at,
    validThrough: job.deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    employmentType: job.role_type === 'Heltid' ? 'FULL_TIME' : 
                    job.role_type === 'Deltid' ? 'PART_TIME' : 'CONTRACTOR',
    hiringOrganization: {
      '@type': 'Organization',
      name: 'Flowingo AB',
      sameAs: 'https://flowingo.se',
      logo: 'https://flowingo.se/logo.png',
    },
    jobLocation: {
      '@type': 'Place',
      address: {
        '@type': 'PostalAddress',
        addressLocality: job.location,
        addressCountry: 'SE',
      },
    },
    experienceRequirements: job.experience_level,
    industry: 'VVS / HVAC',
    occupationalCategory: job.category || 'VVS',
  };
};

/**
 * Generate ItemList schema for category/listing pages
 */
export const generateItemListSchema = (products, language = 'sv', pageUrl = '') => ({
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  '@id': pageUrl ? `${pageUrl}#itemlist` : undefined,
  numberOfItems: products.length,
  itemListElement: products.slice(0, 50).map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    url: `https://flowingo.se/produkter/${p.slug || p.id}`,
    name: language === 'sv' ? p.name_sv : (p.name_en || p.name_sv),
  })),
});

/**
 * Generate schema array for knowledge hub posts.
 * Always includes TechArticle; adds FAQPage/HowTo alongside when applicable.
 */
const defaultAuthorPerson = {
  '@type': 'Person',
  name: 'Rafid Kadhim',
  jobTitle: 'Försäljningsansvarig',
  worksFor: { '@id': 'https://flowingo.se/#organization' },
  sameAs: ['https://www.linkedin.com/in/rafidkadhim/'],
};

export const generateArticleSchema = (article, language = 'sv') => {
  const title = language === 'sv' ? article.title_sv : article.title_en;
  const excerpt = language === 'sv' ? article.excerpt_sv : article.excerpt_en;
  const content = language === 'sv' ? article.content_sv : article.content_en;
  const fullPlain = (content || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const plain = fullPlain.slice(0, 600);
  const url = `https://flowingo.se/kunskap/${article.slug}`;

  const techArticle = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    '@id': `${url}#article`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url,
    },
    headline: title,
    description: excerpt || plain.slice(0, 200),
    image: article.cover_image_url || 'https://flowingo.se/og-image.jpg',
    author: article.author && article.author !== 'Flowingo Redaktion' && article.author !== defaultAuthorPerson.name
      ? { '@type': 'Person', name: article.author }
      : defaultAuthorPerson,
    publisher: {
      '@id': 'https://flowingo.se/#organization',
    },
    datePublished: article.published_at || article.created_at,
    dateModified: article.updated_at || article.created_at,
    inLanguage: language === 'sv' ? 'sv-SE' : 'en-US',
    keywords: (article.tags || []).join(', '),
    articleSection: article.category || 'guide',
    wordCount: fullPlain.split(/\s+/).filter(Boolean).length,
  };

  const schemas = [techArticle];

  // FAQPage schema
  if (article.schema_type === 'FAQPage') {
    const qaPairs = [];
    if (content) {
      const re = /<h3[^>]*>(.*?)<\/h3>\s*<p[^>]*>(.*?)<\/p>/gi;
      let m;
      while ((m = re.exec(content)) !== null) {
        const q = m[1].replace(/<[^>]*>/g, '').trim();
        const a = m[2].replace(/<[^>]*>/g, '').trim();
        if (q && a) qaPairs.push({ q, a });
      }
    }
    if (qaPairs.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        '@id': url,
        mainEntity: qaPairs.map((p) => ({
          '@type': 'Question',
          name: p.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: p.a,
          },
        })),
      });
    }
  }

  // HowTo schema
  if (article.schema_type === 'HowTo') {
    const steps = [];
    if (content) {
      const re = /<h[23][^>]*>(.*?)<\/h[23]>\s*<p[^>]*>(.*?)<\/p>/gi;
      let m;
      let i = 1;
      while ((m = re.exec(content)) !== null) {
        const name = m[1].replace(/<[^>]*>/g, '').trim();
        const text = m[2].replace(/<[^>]*>/g, '').trim();
        if (name) {
          steps.push({
            '@type': 'HowToStep',
            position: i++,
            name,
            text: text || name,
            url: `${url}#step-${i - 1}`,
          });
        }
      }
    }
    if (steps.length > 0) {
      schemas.push({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        '@id': url,
        name: title,
        description: excerpt || plain.slice(0, 200),
        image: article.cover_image_url || 'https://flowingo.se/og-image.jpg',
        totalTime: article.reading_time_minutes ? `PT${article.reading_time_minutes}M` : undefined,
        step: steps,
      });
    }
  }

  return schemas;
};

/**
 * SEO Component
 */
const SEO = ({
  title,
  description,
  canonical,
  type = 'website',
  image,
  noindex = false,
  schema = [],
  language = 'sv',
}) => {
  const location = useLocation();
  
  const fullTitle = title 
    ? `${title} | Flowingo AB` 
    : defaultSEO.defaultTitle;
  
  const fullDescription = description || defaultSEO.defaultDescription;
  const canonicalUrl = canonical || `${defaultSEO.siteUrl}${location.pathname}`;
  const ogImage = image || defaultSEO.defaultImage;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to update or create meta tag
    const updateMeta = (name, content, isProperty = false) => {
      const attr = isProperty ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Basic meta tags
    updateMeta('description', fullDescription);
    updateMeta('robots', noindex ? 'noindex, nofollow' : 'index, follow');
    
    // Open Graph
    updateMeta('og:title', fullTitle, true);
    updateMeta('og:description', fullDescription, true);
    updateMeta('og:type', type, true);
    updateMeta('og:url', canonicalUrl, true);
    updateMeta('og:image', ogImage, true);
    updateMeta('og:site_name', defaultSEO.siteName, true);
    updateMeta('og:locale', language === 'sv' ? 'sv_SE' : 'en_US', true);
    
    // Twitter Card
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:title', fullTitle);
    updateMeta('twitter:description', fullDescription);
    updateMeta('twitter:image', ogImage);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Update hreflang links
    const updateHreflang = (lang, href) => {
      let link = document.querySelector(`link[hreflang="${lang}"]`);
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'alternate');
        link.setAttribute('hreflang', lang);
        document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    };
    updateHreflang('sv', canonicalUrl);
    updateHreflang('en', `${canonicalUrl}?lang=en`);
    updateHreflang('x-default', canonicalUrl);

    // Structured Data (JSON-LD)
    // Remove existing schema scripts
    document.querySelectorAll('script[type="application/ld+json"]').forEach(el => el.remove());

    // Add organization and website schema (always present)
    const addSchema = (schemaData) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schemaData);
      document.head.appendChild(script);
    };

    addSchema(organizationSchema);
    addSchema(websiteSchema);
    
    // Add page-specific schemas
    schema.forEach(s => addSchema(s));

  }, [fullTitle, fullDescription, canonicalUrl, ogImage, type, noindex, schema, language]);

  return null; // This component doesn't render anything
};

export default SEO;
